import { useEffect, useState } from "react";
import {
  listBenchmarks,
  getCppiRecommendations,
  type Benchmark,
  type CppiRecommendation,
  type SymbolRecommendation,
} from "../api/client";

interface Props {
  portfolioId: number | null;
}

// ---- Formatting helpers ----

function fmtCcy(v: number, digits = 0) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: digits,
    minimumFractionDigits: digits,
  }).format(v);
}

function fmtPct(v: number, digits = 1) {
  return (v * 100).toFixed(digits) + "%";
}

function fmtNum(v: number, digits = 2) {
  return v.toFixed(digits);
}

// ---- Small UI primitives ----

interface MetricCardProps {
  label: string;
  value: string;
  sub?: string;
  color?: string;
  bg?: string;
}

function MetricCard({ label, value, sub, color = "#1e293b", bg = "#f8fafc" }: MetricCardProps) {
  return (
    <div
      style={{
        background: bg,
        border: "1px solid #e2e8f0",
        borderRadius: 10,
        padding: "14px 18px",
        flex: "1 1 140px",
        minWidth: 130,
      }}
    >
      <div style={{ fontSize: 11, fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ fontSize: 20, fontWeight: 700, color }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

// ---- Action badge ----

function ActionBadge({ action }: { action: "BUY" | "SELL" | "HOLD" }) {
  const cfg: Record<string, { bg: string; color: string }> = {
    BUY: { bg: "#dcfce7", color: "#16a34a" },
    SELL: { bg: "#fee2e2", color: "#dc2626" },
    HOLD: { bg: "#f1f5f9", color: "#64748b" },
  };
  const { bg, color } = cfg[action] ?? cfg.HOLD;
  return (
    <span
      style={{
        background: bg,
        color,
        borderRadius: 4,
        padding: "2px 8px",
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: "0.06em",
      }}
    >
      {action}
    </span>
  );
}

// ---- Label + input ----

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 12, color: "#475569", fontWeight: 600 }}>
      {label}
      {children}
    </label>
  );
}

const inputCss: React.CSSProperties = {
  padding: "6px 10px",
  border: "1px solid #cbd5e1",
  borderRadius: 6,
  fontSize: 13,
  color: "#1e293b",
  background: "#fff",
  width: "100%",
};

// ---- Progress bar ----

function MiniBar({ value, max = 1, color = "#3b82f6" }: { value: number; max?: number; color?: string }) {
  const pct = Math.min(1, Math.max(0, value / max)) * 100;
  return (
    <div style={{ background: "#e2e8f0", borderRadius: 4, height: 6, width: "100%", overflow: "hidden" }}>
      <div style={{ background: color, width: `${pct}%`, height: "100%", borderRadius: 4, transition: "width 0.5s" }} />
    </div>
  );
}

// ============================================================

export default function RecommendationsPage({ portfolioId }: Props) {
  // Benchmarks for selector
  const [benchmarks, setBenchmarks] = useState<Benchmark[]>([]);
  const [loadingBm, setLoadingBm] = useState(false);

  // Parameters (controlled)
  const [selectedBmId, setSelectedBmId] = useState<number | null>(null);
  const [floorPct, setFloorPct] = useState<string>("80");
  const [lookbackMonths, setLookbackMonths] = useState<string>("6");
  const [rfRate, setRfRate] = useState<string>("4");
  const [maxMultiplier, setMaxMultiplier] = useState<string>("10");

  // Results
  const [rec, setRec] = useState<CppiRecommendation | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showRisky, setShowRisky] = useState(true);
  const [showSafe, setShowSafe] = useState(false);

  // Load benchmarks when portfolio changes
  useEffect(() => {
    if (!portfolioId) return;
    setLoadingBm(true);
    setRec(null);
    setError(null);
    listBenchmarks(portfolioId)
      .then((res) => {
        setBenchmarks(res.data);
        setSelectedBmId(res.data.length > 0 ? res.data[0].id : null);
      })
      .catch((err) => setError(err?.response?.data?.detail ?? err.message))
      .finally(() => setLoadingBm(false));
  }, [portfolioId]);

  const handleRun = async () => {
    if (!portfolioId || !selectedBmId) return;
    setLoading(true);
    setError(null);
    setRec(null);

    const fp = parseFloat(floorPct) / 100;
    const lm = parseInt(lookbackMonths, 10);
    const rf = parseFloat(rfRate) / 100;
    const mm = parseFloat(maxMultiplier);

    if (isNaN(fp) || isNaN(lm) || isNaN(rf) || isNaN(mm)) {
      setError("Please check all parameter values.");
      setLoading(false);
      return;
    }

    try {
      const res = await getCppiRecommendations(portfolioId, {
        benchmark_id: selectedBmId,
        floor_pct: fp,
        lookback_months: lm,
        risk_free_rate: rf,
        max_multiplier: mm,
      });
      setRec(res.data);
    } catch (err: unknown) {
      const e = err as { response?: { data?: { detail?: string } }; message?: string };
      setError(e?.response?.data?.detail ?? e?.message ?? "Request failed.");
    } finally {
      setLoading(false);
    }
  };

  if (!portfolioId) {
    return (
      <div style={{ color: "#94a3b8", padding: 32 }}>Select a portfolio to view recommendations.</div>
    );
  }

  // ---- Build filtered recommendation rows ----
  const allRows: SymbolRecommendation[] = rec?.recommendations ?? [];
  const riskyRows = allRows.filter((r) => r.is_risky);
  const safeRows = allRows.filter((r) => !r.is_risky);
  const visibleRows = [
    ...(showRisky ? riskyRows : []),
    ...(showSafe ? safeRows : []),
  ];

  const totalBuy = riskyRows.filter((r) => r.trade_amount > 0).reduce((s, r) => s + r.trade_amount, 0);
  const totalSell = riskyRows.filter((r) => r.trade_amount < 0).reduce((s, r) => s + r.trade_amount, 0);

  return (
    <div>
      <h2 style={{ margin: "0 0 4px", color: "#1e293b" }}>CPPI Recommendations</h2>
      <p style={{ margin: "0 0 20px", fontSize: 13, color: "#64748b" }}>
        Constant Proportion Portfolio Insurance with Karatzas/Merton dynamic multiplier
        m&nbsp;=&nbsp;(μ&nbsp;−&nbsp;r) / σ². Risky allocation&nbsp;= m&nbsp;×&nbsp;cushion,
        cushion&nbsp;= NAV&nbsp;−&nbsp;floor, floor&nbsp;= floor&nbsp;%&nbsp;×&nbsp;benchmark.
      </p>

      {/* ---- Parameters panel ---- */}
      <div
        style={{
          background: "#fff",
          border: "1px solid #e2e8f0",
          borderRadius: 10,
          padding: "18px 20px",
          marginBottom: 24,
        }}
      >
        <div style={{ fontWeight: 700, fontSize: 14, color: "#1e293b", marginBottom: 14 }}>Parameters</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 16, alignItems: "flex-end" }}>
          <Field label="Benchmark (floor reference)">
            <select
              value={selectedBmId ?? ""}
              onChange={(e) => setSelectedBmId(Number(e.target.value))}
              disabled={loadingBm}
              style={{ ...inputCss, width: 220 }}
            >
              {loadingBm && <option value="">Loading…</option>}
              {!loadingBm && benchmarks.length === 0 && (
                <option value="">No benchmarks — create one first</option>
              )}
              {benchmarks.map((bm) => (
                <option key={bm.id} value={bm.id}>
                  {bm.name}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Floor % of benchmark">
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <input
                type="number"
                value={floorPct}
                min={0}
                max={100}
                step={1}
                onChange={(e) => setFloorPct(e.target.value)}
                style={{ ...inputCss, width: 70 }}
              />
              <span style={{ color: "#64748b", fontSize: 13 }}>%</span>
            </div>
          </Field>

          <Field label="Lookback">
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <input
                type="number"
                value={lookbackMonths}
                min={1}
                max={60}
                step={1}
                onChange={(e) => setLookbackMonths(e.target.value)}
                style={{ ...inputCss, width: 60 }}
              />
              <span style={{ color: "#64748b", fontSize: 13 }}>months</span>
            </div>
          </Field>

          <Field label="Risk-free rate">
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <input
                type="number"
                value={rfRate}
                min={0}
                max={100}
                step={0.25}
                onChange={(e) => setRfRate(e.target.value)}
                style={{ ...inputCss, width: 70 }}
              />
              <span style={{ color: "#64748b", fontSize: 13 }}>%</span>
            </div>
          </Field>

          <Field label="Max multiplier">
            <input
              type="number"
              value={maxMultiplier}
              min={0}
              max={50}
              step={1}
              onChange={(e) => setMaxMultiplier(e.target.value)}
              style={{ ...inputCss, width: 70 }}
            />
          </Field>

          <button
            onClick={handleRun}
            disabled={loading || !selectedBmId}
            style={{
              padding: "7px 22px",
              background: "#3b82f6",
              color: "#fff",
              border: "none",
              borderRadius: 7,
              cursor: loading || !selectedBmId ? "not-allowed" : "pointer",
              fontWeight: 700,
              fontSize: 14,
              opacity: loading || !selectedBmId ? 0.6 : 1,
              whiteSpace: "nowrap",
            }}
          >
            {loading ? "Computing…" : "Run Analysis"}
          </button>
        </div>
      </div>

      {/* ---- Error ---- */}
      {error && (
        <div
          style={{
            background: "#fef2f2",
            border: "1px solid #fca5a5",
            borderRadius: 8,
            padding: "12px 16px",
            color: "#dc2626",
            marginBottom: 20,
            fontSize: 13,
          }}
        >
          {error}
        </div>
      )}

      {/* ---- Results ---- */}
      {rec && (
        <>
          {/* ---- Metrics overview ---- */}
          <div
            style={{
              background: "#fff",
              border: "1px solid #e2e8f0",
              borderRadius: 10,
              padding: "18px 20px",
              marginBottom: 20,
            }}
          >
            <div style={{ fontWeight: 700, fontSize: 14, color: "#1e293b", marginBottom: 14 }}>
              Portfolio Overview — <span style={{ fontWeight: 400, color: "#64748b" }}>{rec.as_of_date}</span>
            </div>

            {/* Row 1: NAV / benchmark / floor / cushion */}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 12 }}>
              <MetricCard label="Portfolio NAV" value={fmtCcy(rec.portfolio_nav)} />
              <MetricCard
                label={`Benchmark (${rec.benchmark_name})`}
                value={fmtCcy(rec.benchmark_value)}
                bg="#f0f9ff"
              />
              <MetricCard
                label={`Floor (${fmtPct(rec.floor_pct, 0)} × BM)`}
                value={fmtCcy(rec.floor)}
                bg="#fffbeb"
              />
              <MetricCard
                label="Cushion"
                value={fmtCcy(rec.cushion)}
                sub={fmtPct(rec.cushion_pct) + " of NAV"}
                color={rec.cushion < 0 ? "#dc2626" : "#16a34a"}
                bg={rec.cushion < 0 ? "#fef2f2" : "#f0fdf4"}
              />
            </div>

            {/* Row 2: allocation summary */}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 16 }}>
              <MetricCard
                label="Current Risky"
                value={fmtCcy(rec.current_risky_allocation)}
                sub={fmtPct(rec.current_risky_pct) + " of NAV"}
              />
              <MetricCard
                label="Target Risky"
                value={fmtCcy(rec.target_risky_allocation)}
                sub={fmtPct(rec.target_risky_pct) + " of NAV"}
                color="#3b82f6"
                bg="#eff6ff"
              />
              <MetricCard
                label="Multiplier m (raw → capped)"
                value={`${fmtNum(rec.multiplier)} × cushion`}
                sub={`Raw: ${fmtNum(rec.multiplier_raw)} | Cap: ${rec.max_multiplier}`}
                color={rec.multiplier > 5 ? "#d97706" : "#1e293b"}
              />
              <MetricCard
                label="Net Risky Change"
                value={fmtCcy(rec.target_risky_allocation - rec.current_risky_allocation)}
                color={rec.target_risky_allocation > rec.current_risky_allocation ? "#16a34a" : "#dc2626"}
              />
            </div>

            {/* Visual bars */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 24px", maxWidth: 600 }}>
              <div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "#64748b", marginBottom: 3 }}>
                  <span>Current risky allocation</span>
                  <span>{fmtPct(rec.current_risky_pct)}</span>
                </div>
                <MiniBar value={rec.current_risky_pct} color="#94a3b8" />
              </div>
              <div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "#3b82f6", marginBottom: 3 }}>
                  <span>Target risky allocation</span>
                  <span>{fmtPct(rec.target_risky_pct)}</span>
                </div>
                <MiniBar value={rec.target_risky_pct} color="#3b82f6" />
              </div>
              <div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "#64748b", marginBottom: 3 }}>
                  <span>Cushion / NAV</span>
                  <span>{fmtPct(rec.cushion_pct)}</span>
                </div>
                <MiniBar value={rec.cushion_pct} color={rec.cushion_pct < 0.1 ? "#f59e0b" : "#10b981"} />
              </div>
            </div>
          </div>

          {/* ---- Risk / drift metrics ---- */}
          <div
            style={{
              background: "#fff",
              border: "1px solid #e2e8f0",
              borderRadius: 10,
              padding: "18px 20px",
              marginBottom: 20,
            }}
          >
            <div style={{ fontWeight: 700, fontSize: 14, color: "#1e293b", marginBottom: 14 }}>
              Risky Sub-Portfolio Risk Estimates
              <span style={{ fontWeight: 400, fontSize: 12, color: "#94a3b8", marginLeft: 8 }}>
                ({rec.obs_count} trading-day obs, {rec.lookback_months}m lookback)
              </span>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
              <MetricCard
                label="Annualised drift μ"
                value={fmtPct(rec.risky_mu, 2)}
                sub="weighted mean return × 252"
                color={rec.risky_mu > rec.risk_free_rate ? "#16a34a" : "#dc2626"}
              />
              <MetricCard
                label="Annualised vol σ"
                value={fmtPct(rec.risky_sigma, 2)}
                sub="√(var × 252)"
              />
              <MetricCard
                label="Risk-free rate r"
                value={fmtPct(rec.risk_free_rate, 2)}
                sub="annualised input"
              />
              <MetricCard
                label="Excess return μ − r"
                value={fmtPct(rec.risky_mu - rec.risk_free_rate, 2)}
                color={rec.risky_mu > rec.risk_free_rate ? "#16a34a" : "#dc2626"}
              />
              <MetricCard
                label="Raw multiplier (μ−r)/σ²"
                value={fmtNum(rec.multiplier_raw)}
                sub={`Capped to ${fmtNum(rec.multiplier)}`}
              />
            </div>
          </div>

          {/* ---- Warnings ---- */}
          {rec.data_warnings.length > 0 && (
            <div
              style={{
                background: "#fffbeb",
                border: "1px solid #fcd34d",
                borderRadius: 8,
                padding: "12px 16px",
                marginBottom: 20,
              }}
            >
              <div style={{ fontWeight: 700, color: "#92400e", marginBottom: 6, fontSize: 13 }}>⚠ Data Warnings</div>
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: "#78350f", lineHeight: 1.7 }}>
                {rec.data_warnings.map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
              </ul>
            </div>
          )}

          {/* ---- Trade summary ---- */}
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 12,
              marginBottom: 16,
              alignItems: "center",
            }}
          >
            <div
              style={{
                background: "#f0fdf4",
                border: "1px solid #86efac",
                borderRadius: 8,
                padding: "8px 16px",
                fontSize: 13,
              }}
            >
              <span style={{ color: "#64748b" }}>Total to BUY: </span>
              <strong style={{ color: "#16a34a" }}>{fmtCcy(totalBuy)}</strong>
            </div>
            <div
              style={{
                background: "#fef2f2",
                border: "1px solid #fca5a5",
                borderRadius: 8,
                padding: "8px 16px",
                fontSize: 13,
              }}
            >
              <span style={{ color: "#64748b" }}>Total to SELL: </span>
              <strong style={{ color: "#dc2626" }}>{fmtCcy(totalSell)}</strong>
            </div>
            <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
              <button
                onClick={() => setShowRisky((v) => !v)}
                style={{
                  padding: "5px 12px",
                  borderRadius: 6,
                  border: "1px solid #cbd5e1",
                  background: showRisky ? "#3b82f6" : "#f8fafc",
                  color: showRisky ? "#fff" : "#475569",
                  cursor: "pointer",
                  fontSize: 12,
                  fontWeight: 600,
                }}
              >
                Risky ({riskyRows.length})
              </button>
              <button
                onClick={() => setShowSafe((v) => !v)}
                style={{
                  padding: "5px 12px",
                  borderRadius: 6,
                  border: "1px solid #cbd5e1",
                  background: showSafe ? "#3b82f6" : "#f8fafc",
                  color: showSafe ? "#fff" : "#475569",
                  cursor: "pointer",
                  fontSize: 12,
                  fontWeight: 600,
                }}
              >
                Safe ({safeRows.length})
              </button>
            </div>
          </div>

          {/* ---- Recommendations table ---- */}
          <div
            style={{
              background: "#fff",
              border: "1px solid #e2e8f0",
              borderRadius: 10,
              overflow: "hidden",
            }}
          >
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "#f8fafc" }}>
                  {[
                    "Symbol",
                    "Description",
                    "Class",
                    "Current Price",
                    "Current Value",
                    "Risky Weight",
                    "Target Value",
                    "Trade Amount",
                    "Shares",
                    "Action",
                    "Data",
                  ].map((h) => (
                    <th
                      key={h}
                      style={{
                        textAlign: h === "Symbol" || h === "Description" || h === "Action" || h === "Data" || h === "Class"
                          ? "left"
                          : "right",
                        padding: "8px 12px",
                        borderBottom: "1px solid #e2e8f0",
                        color: "#475569",
                        fontWeight: 600,
                        fontSize: 12,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibleRows.length === 0 && (
                  <tr>
                    <td colSpan={11} style={{ padding: 20, color: "#94a3b8", textAlign: "center" }}>
                      No rows. Toggle Risky / Safe above.
                    </td>
                  </tr>
                )}
                {visibleRows.map((row, idx) => {
                  const changeColor =
                    row.trade_amount > 0.5
                      ? "#16a34a"
                      : row.trade_amount < -0.5
                      ? "#dc2626"
                      : "#94a3b8";

                  return (
                    <tr
                      key={`${row.symbol}-${idx}`}
                      style={{
                        borderBottom: "1px solid #f1f5f9",
                        background: !row.is_risky ? "#fafafa" : "transparent",
                      }}
                    >
                      <td style={{ padding: "8px 12px", fontFamily: "monospace", fontWeight: 600 }}>
                        {row.symbol}
                      </td>
                      <td style={{ padding: "8px 12px", color: "#64748b", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {row.description ?? "—"}
                      </td>
                      <td style={{ padding: "8px 12px", color: "#64748b" }}>
                        <span
                          style={{
                            background: row.is_risky ? "#eff6ff" : "#f1f5f9",
                            color: row.is_risky ? "#2563eb" : "#64748b",
                            borderRadius: 4,
                            padding: "1px 6px",
                            fontSize: 11,
                            fontWeight: 600,
                          }}
                        >
                          {row.asset_class ?? "—"}
                        </span>
                      </td>
                      <td style={{ padding: "8px 12px", textAlign: "right", fontFamily: "monospace", color: "#64748b" }}>
                        {row.current_price != null ? fmtCcy(row.current_price, 2) : "—"}
                      </td>
                      <td style={{ padding: "8px 12px", textAlign: "right", fontFamily: "monospace" }}>
                        {fmtCcy(row.current_value)}
                      </td>
                      <td style={{ padding: "8px 12px", textAlign: "right", color: "#64748b" }}>
                        {row.is_risky ? fmtPct(row.current_weight_in_risky) : "—"}
                      </td>
                      <td style={{ padding: "8px 12px", textAlign: "right", fontFamily: "monospace", color: "#3b82f6" }}>
                        {fmtCcy(row.target_value)}
                      </td>
                      <td
                        style={{
                          padding: "8px 12px",
                          textAlign: "right",
                          fontFamily: "monospace",
                          fontWeight: 600,
                          color: changeColor,
                        }}
                      >
                        {row.trade_amount > 0 ? "+" : ""}
                        {fmtCcy(row.trade_amount)}
                      </td>
                      <td
                        style={{
                          padding: "8px 12px",
                          textAlign: "right",
                          fontFamily: "monospace",
                          fontWeight: 700,
                          color: changeColor,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {row.trade_shares != null && Math.abs(row.trade_shares) >= 0.0001
                          ? `${row.trade_shares > 0 ? "+" : ""}${Number(row.trade_shares).toLocaleString("en-US", { maximumFractionDigits: 4 })}`
                          : "—"}
                      </td>
                      <td style={{ padding: "8px 12px" }}>
                        <ActionBadge action={row.action} />
                      </td>
                      <td style={{ padding: "8px 12px", color: row.has_price_data ? "#16a34a" : "#94a3b8", fontSize: 11 }}>
                        {row.has_price_data ? "✓ hist" : row.is_risky ? "no data" : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* ---- Methodology footnote ---- */}
          <div
            style={{
              marginTop: 16,
              padding: "10px 16px",
              background: "#f8fafc",
              border: "1px solid #e2e8f0",
              borderRadius: 8,
              fontSize: 11,
              color: "#94a3b8",
              lineHeight: 1.6,
            }}
          >
            <strong style={{ color: "#64748b" }}>Methodology: </strong>
            CPPI floor&nbsp;=&nbsp;{fmtPct(rec.floor_pct, 0)}&nbsp;×&nbsp;{rec.benchmark_name}.
            Multiplier&nbsp;m&nbsp;=&nbsp;max(0,&nbsp;min((μ−r)/σ²,&nbsp;{rec.max_multiplier}))
            &nbsp;=&nbsp;{fmtNum(rec.multiplier)}.&nbsp;
            Target&nbsp;risky&nbsp;=&nbsp;m&nbsp;×&nbsp;(NAV&nbsp;−&nbsp;floor)&nbsp;
            =&nbsp;{fmtNum(rec.multiplier)}&nbsp;×&nbsp;{fmtCcy(rec.cushion)}&nbsp;
            =&nbsp;{fmtCcy(rec.target_risky_allocation)}.&nbsp;
            Within the risky pool, current weights are maintained.&nbsp;
            μ and σ² estimated from {rec.obs_count} daily returns over the last {rec.lookback_months} months.
            Ref: Karatzas (1989), Black &amp; Jones (1987).
          </div>
        </>
      )}
    </div>
  );
}
